// Property module parser — normalizes admin-service wire DTOs to camelCase
// view-model types consumed everywhere else in the module.
// Only parsed data is allowed past this file; downstream code never reads
// raw API fields.

import _get from 'lodash/get'
import { pickNumber, pickString } from '@shared/utils'
import type { PaginatedResponse } from '@shared/types/pagination'
import type {
  AuditLogDto,
  PropertyActivityResponseDto,
  PropertyActivityRowDto,
  PropertiesApiParamsDto,
  PropertyAmenityItemDto,
  PropertyCoOwnerDto,
  PropertyOwnershipSourceDto,
  PropertyCoOwnerPoaDto,
  PropertyDocumentItemDto,
  PropertyFinancialsDto,
  PropertyHighlightItemDto,
  PropertyMediaItemDto,
  PropertyOfferingDto,
  PropertySettingDto,
  PropertyValuationDto,
  PropertyVaspReadinessDto,
  RefundItemDto,
} from './propertyContracts'
import type {
  AuditLog,
  PropertyActivityEvent,
  CoOwnerComplianceStatus,
  PropertiesApiParams,
  PropertiesListResponse,
  Property,
  PropertyAmenityItem,
  PropertyCoOwner,
  PropertyOwnershipSource,
  PropertyCoOwnerPoa,
  PropertyDocumentItem,
  PropertyFinancials,
  PropertyHighlightItem,
  PropertyMasterItem,
  PropertyMastersResponse,
  PropertyMediaItem,
  PropertyOffering,
  PropertySetting,
  PropertyValuation,
  PropertyVaspBlock,
  PropertyVaspOnboarding,
  PropertyVaspReadiness,
  RefundItem,
  RentalPeriod,
} from '../types/propertyTypes'

// ─── Property record ────────────────────────────────────────────────────────

export const parseProperty = (data: unknown): Property => {
  const raw = (data ?? {}) as Record<string, unknown>
  const financials =
    raw.financials && typeof raw.financials === 'object'
      ? parsePropertyFinancials(raw.financials as PropertyFinancialsDto)
      : null

  const propertyName = String(_get(raw, 'propertyName') ?? _get(raw, 'name') ?? 'Unknown Property')
  const propertyType = String(_get(raw, 'propertyType') ?? _get(raw, 'type') ?? 'N/A')
  const city = String(_get(raw, 'city') ?? _get(raw, 'location') ?? 'N/A')
  const valuation = _get(raw, 'dldValuation') ?? _get(raw, 'totalValue') ?? '0'
  const dldValuation = isNaN(parseFloat(String(valuation)))
    ? String(valuation)
    : parseFloat(String(valuation))
  const numberOfUnits = Number(_get(raw, 'numberOfUnits') ?? _get(raw, 'units') ?? 0) || 0
  const propertyStatus = String(_get(raw, 'propertyStatus') ?? _get(raw, 'status') ?? 'Unknown')
  const createdAt = String(_get(raw, 'createdAt') ?? _get(raw, 'date') ?? '')

  return {
    id: String(_get(raw, 'id', '')),
    propertyName,
    propertyType,
    propertyStatus,
    city,
    dldValuation,
    numberOfUnits,
    createdAt,
    addressLine1: pickString(raw, 'addressLine1'),
    addressLine2: pickString(raw, 'addressLine2'),
    stateProvince: pickString(raw, 'stateProvince'),
    postalCode: pickString(raw, 'postalCode'),
    googleMapsUrl: pickString(raw, 'googleMapsUrl'),
    propertyDescription: pickString(raw, 'propertyDescription'),
    propertyShortDescription: pickString(raw, 'propertyShortDescription'),
    propertyRefCode: pickString(raw, 'propertyRefCode'),
    buildingName: pickString(raw, 'buildingName'),
    areaName: pickString(raw, 'areaName'),
    villaNumber: pickString(raw, 'villaNumber'),
    landNumber: pickString(raw, 'landNumber'),
    developerName: pickString(raw, 'developerName'),
    projectName: pickString(raw, 'projectName'),
    titleDeedNumber: pickString(raw, 'titleDeedNumber'),
    certificateYear: pickString(raw, 'certificateYear'),
    dldPermitNumber: pickString(raw, 'dldPermitNumber'),
    dldValuationDate: pickString(raw, 'dldValuationDate'),
    dubaiMunicipalityNumber: pickString(raw, 'dubaiMunicipalityNumber'),
    latitude: pickNumber(raw, 'latitude', 'latitude'),
    longitude: pickNumber(raw, 'longitude', 'longitude'),
    actualAreaSqm: pickNumber(raw, 'actualAreaSqm'),
    builtUpAreaSqm: pickNumber(raw, 'builtUpAreaSqm'),
    commonAreaSqm: pickNumber(raw, 'commonAreaSqm'),
    sharedAreaSqm: pickNumber(raw, 'sharedAreaSqm'),
    yearBuilt: pickNumber(raw, 'yearBuilt'),
    numberOfBedrooms: pickNumber(raw, 'numberOfBedrooms'),
    numberOfBathrooms: pickNumber(raw, 'numberOfBathrooms'),
    parkingSpaces: pickNumber(raw, 'parkingSpaces'),
    floorNumber: pickNumber(raw, 'floorNumber'),
    unitNumber: pickString(raw, 'unitNumber'),
    isFurnished: raw.isFurnished === true,
    countryId: pickString(raw, 'countryId'),
    marketId: pickString(raw, 'marketId'),
    rejectionReason: raw.rejectionReason ? String(raw.rejectionReason) : undefined,
    rejectionAttachment: raw.rejectionAttachment ? String(raw.rejectionAttachment) : undefined,
    resubmissionStatus: raw.resubmissionStatus ? String(raw.resubmissionStatus) : undefined,
    ownerName: raw.ownerName ? String(raw.ownerName) : undefined,
    activeOrdersCount: raw.activeOrdersCount ? Number(raw.activeOrdersCount) : undefined,
    delistedOn: raw.delistedOn ? String(raw.delistedOn) : undefined,
    delistedBy: raw.delistedBy ? String(raw.delistedBy) : undefined,
    refundStatus: raw.refundStatus ? String(raw.refundStatus) : undefined,
    submittedAt: raw.submittedAt ? String(raw.submittedAt) : undefined,
    approvedAt: raw.approvedAt ? String(raw.approvedAt) : undefined,
    // Dropped until now, so every "Sent to VAISP" / "Days in VAISP" read blank
    // or fell back to the submission date (VA-03 / VA-04).
    sentToVaspAt: raw.sentToVaspAt ? String(raw.sentToVaspAt) : undefined,
    listedAt: raw.listedAt ? String(raw.listedAt) : undefined,

    // ── Lifecycle columns ────────────────────────────────────────────────────
    // PropertyDto declares these in snake_case (the admin-service GET endpoint
    // still returns them snake_case). Fallbacks ensure the parser handles both
    // the current snake_case wire shape and any future camelCase migration.
    addressId: pickString(raw, 'addressId', 'address_id'),
    currencyCode: pickString(raw, 'currencyCode', 'currency_code'),
    view: pickString(raw, 'view'),
    ownershipType: pickString(raw, 'ownershipType', 'ownership_type'),
    ownershipTypeRemainingYears: pickNumber(
      raw,
      'ownershipTypeRemainingYears',
      'ownership_type_remaining_years',
    ),
    propertyCondition: pickString(raw, 'propertyCondition', 'property_condition'),
    makaniNumber: pickString(raw, 'makaniNumber', 'makani_number'),
    buildingConfiguration: pickString(raw, 'buildingConfiguration', 'building_configuration'),
    hasWhiteGoods: raw.hasWhiteGoods === true || raw.has_white_goods === true,
    propertyUpgrades: pickString(raw, 'propertyUpgrades', 'property_upgrades'),
    remarks: pickString(raw, 'remarks'),

    // ── Derived capability flags + transitions (BE-supplied; §5) ─────────────
    // The detail DTO emits camelCase (data-fetching rule); the snake_case
    // fallback covers the window before the new backend is deployed to dev.
    opsEnrichmentComplete: pickBool(raw, 'opsEnrichmentComplete', 'ops_enrichment_complete'),
    financialsComplete: pickBool(raw, 'financialsComplete', 'financials_complete'),
    financials,
    canSubmitForReview: pickBool(raw, 'canSubmitForReview', 'can_submit_for_review'),
    canSubmitForVerification: pickBool(
      raw,
      'canSubmitForVerification',
      'can_submit_for_verification',
    ),
    canArchive: pickBool(raw, 'canArchive', 'can_archive'),
    availableTransitions: pickStringArray(raw, 'availableTransitions', 'available_transitions'),

    // VASP lifecycle block (Ctrl-Alt v2) — emitted on both read routes.
    vaspOnboarding: parsePropertyVaspBlock(raw),

    // Pre-flight onboarding readiness — detail read only (undefined on list rows).
    vaspReadiness: parsePropertyVaspReadiness(raw),
  }
}

/** Coerce any wire value to a trimmed string, or null when empty/absent. */
function strOrNull(v: unknown): string | null {
  return v == null || v === '' ? null : String(v)
}

/** Read a boolean from either the camelCase or snake_case key. Returns undefined when absent. */
function pickBool(
  raw: Record<string, unknown>,
  camelKey: string,
  snakeKey?: string,
): boolean | undefined {
  const v = raw[camelKey] ?? (snakeKey ? raw[snakeKey] : undefined)
  return v === undefined ? undefined : v === true
}

/** Read a string[] from either the camelCase or snake_case key. */
function pickStringArray(
  raw: Record<string, unknown>,
  camelKey: string,
  snakeKey?: string,
): string[] | undefined {
  const v = raw[camelKey] ?? (snakeKey ? raw[snakeKey] : undefined)
  return Array.isArray(v) ? v.map(String) : undefined
}

// ─── Rental periods ───────────────────────────────────────────────────────────

export const parseRentalPeriod = (data: unknown): RentalPeriod => {
  const raw = (data ?? {}) as Record<string, unknown>
  const status = String(_get(raw, 'status') ?? 'vacant') as RentalPeriod['status']
  return {
    id: pickString(raw, 'id'),
    status: status === 'leased' ? 'leased' : 'vacant',
    tenantType: (pickString(raw, 'tenantType') as RentalPeriod['tenantType']) || null,
    monthlyRentAmount: pickNumber(raw, 'monthlyRentAmount') ?? null,
    annualRentAmount: pickNumber(raw, 'annualRentAmount') ?? null,
    leaseStart: pickString(raw, 'leaseStart') || null,
    leaseEnd: pickString(raw, 'leaseEnd') || null,
    rentAgreementDocumentId: pickString(raw, 'rentAgreementDocumentId') || null,
    source: pickString(raw, 'source') || null,
    notes: pickString(raw, 'notes') || null,
    isLatest: raw.isLatest === true,
    createdAt: pickString(raw, 'createdAt'),
  }
}

export const parsePropertiesList = (list: unknown[]): Property[] =>
  Array.isArray(list) ? list.map(parseProperty) : []

export const parsePropertiesListResponse = (data: unknown): PropertiesListResponse => {
  const rows = _get(data, 'data.rows', []) as unknown[]
  const parsed = parsePropertiesList(Array.isArray(rows) ? rows : [])
  const total =
    _get(data, 'data.meta.total') ??
    _get(data, 'data.total') ??
    _get(data, 'data.rowsCount') ??
    _get(data, 'data.count') ??
    parsed.length

  return {
    data: parsed,
    total: Number(total) || 0,
    page: Number(_get(data, 'data.meta.page') ?? _get(data, 'data.page', 1)) || 1,
    limit: Number(_get(data, 'data.meta.limit') ?? _get(data, 'data.limit', 20)) || 20,
  }
}

// ─── Masters (markets / countries) ──────────────────────────────────────────

export const parsePropertyMastersResponse = (data: unknown): PropertyMastersResponse => {
  const itemsSource = extractTabArray(data)
  const items = itemsSource as Array<Record<string, unknown>>

  return {
    message: String(_get(data, 'message', '')),
    data: items
      .map((item): PropertyMasterItem => {
        const id = String(
          item.id ?? item.marketId ?? item.countryId ?? item.code ?? item.value ?? '',
        )
        const name = String(
          item.name ??
            item.marketName ??
            item.countryName ??
            item.label ??
            item.title ??
            item.code ??
            id,
        )
        const code = item.code ?? item.marketCode ?? item.countryCode
        return {
          id,
          name,
          code: code ? String(code) : undefined,
          currencyCode: pickString(item, 'currencyCode'),
          currencyName: pickString(item, 'currencyName'),
          currencySymbol: pickString(item, 'currencySymbol'),
        }
      })
      .filter((item) => item.id),
  }
}

// ─── Per-tab parsers ────────────────────────────────────────────────────────

// Admin service emits numeric fields on /financials and /valuations as
// strings ("11.00") for several columns. Coerce here so downstream
// consumers can safely arithmetic on the values (anti-corruption §12).
function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

export const parsePropertyFinancials = (dto: PropertyFinancialsDto): PropertyFinancials => ({
  id: String(dto.id ?? ''),
  purchasePrice: toNumberOrNull(dto.purchasePrice),
  purchaseDate: (dto.purchaseDate ?? null) as string | null,
  currentMarketValue: toNumberOrNull(dto.currentMarketValue),
  totalOfferingValue: toNumberOrNull(dto.totalOfferingValue),
  grossYieldPercentage: toNumberOrNull(dto.grossYieldPercentage),
  netYieldPercentage: toNumberOrNull(dto.netYieldPercentage),
  annualRentalIncome: toNumberOrNull(dto.annualRentalIncome),
  serviceChargePerSqm: toNumberOrNull(dto.serviceChargePerSqm),
  serviceChargeUnitBasis: dto.serviceChargeUnitBasis ?? null,
  annualServiceChargeTotal: toNumberOrNull(dto.annualServiceChargeTotal),
  mortgageOutstanding: toNumberOrNull(dto.mortgageOutstanding),
  isMortgaged: Boolean(dto.isMortgaged),
  financialNotes: (dto.financialNotes ?? null) as string | null,
  occupancyRateAssumptionPct: toNumberOrNull(dto.occupancyRateAssumptionPct),
  annualRentEscalationPct: toNumberOrNull(dto.annualRentEscalationPct),
  financialsApprovedAt: (dto.financialsApprovedAt ?? null) as string | null,
  financialsApprovedBy: (dto.financialsApprovedBy ?? null) as string | null,
})

function parseComplianceStatus(
  val: string | null | undefined,
): CoOwnerComplianceStatus | undefined {
  if (val == null) return 'pending'
  const s = val.toLowerCase()
  if (s === 'approved' || s === 'rejected' || s === 'expired') return s
  // Treat any truthy unrecognised value as is; null/undefined → pending
  return s as CoOwnerComplianceStatus
}

export const parsePropertyOwnershipSource = (
  dto: PropertyOwnershipSourceDto,
): PropertyOwnershipSource => ({
  isManagedByListingRequest: Boolean(dto.isManagedByListingRequest),
  listingRequestId: dto.listingRequestId ? String(dto.listingRequestId) : null,
  requestRefCode: dto.requestRefCode ? String(dto.requestRefCode) : null,
})

export const parsePropertyCoOwner = (dto: PropertyCoOwnerDto): PropertyCoOwner => ({
  id: dto.id ? String(dto.id) : undefined,
  propertyOwnerId: String(dto.propertyOwnerId ?? ''),
  ownershipPercentage: Number(dto.ownershipPercentage ?? 0),
  bankAccountId: String(dto.bankAccountId ?? ''),
  bankAccountLabel: dto.bankAccountLabel ? String(dto.bankAccountLabel) : null,
  isPrimary: Boolean(dto.isPrimary),
  ownerName: dto.ownerName ? String(dto.ownerName) : undefined,
  ownerPhoneNumber: dto.ownerPhoneNumber ? String(dto.ownerPhoneNumber) : undefined,
  ownerEmail: dto.ownerEmail ? String(dto.ownerEmail) : undefined,
  kycStatus: parseComplianceStatus(String(dto.kycStatus ?? '') || null),
  amlStatus: parseComplianceStatus(String(dto.amlStatus ?? '') || null),
  pepStatus: parseComplianceStatus(String(dto.pepStatus ?? '') || null),
  overallStatus: parseComplianceStatus(String(dto.overallStatus ?? '') || null),
})

// ─── Power of Attorney (POA) ──────────────────────────────────────────────────

export const parsePropertyCoOwnerPoa = (dto: PropertyCoOwnerPoaDto): PropertyCoOwnerPoa => ({
  id: String(dto.id ?? ''),
  coOwnerPoaId: String(dto.coOwnerPoaId ?? ''),
  representsCoOwnerId: String(dto.representsCoOwnerId ?? ''),
  poaDocumentId: dto.poaDocumentId != null ? String(dto.poaDocumentId) : null,
  createdById: dto.createdById ?? null,
  createdByType: dto.createdByType ?? null,
  updatedById: dto.updatedById ?? null,
  updatedByType: dto.updatedByType ?? null,
  createdAt: dto.createdAt ?? null,
  updatedAt: dto.updatedAt ?? null,
})

export const parsePropertyCoOwnerPoaList = (data: unknown): PropertyCoOwnerPoa[] => {
  const rows = extractTabArray(data) as PropertyCoOwnerPoaDto[]
  return rows.map(parsePropertyCoOwnerPoa)
}

export const parsePropertyMediaItem = (dto: PropertyMediaItemDto): PropertyMediaItem => ({
  id: dto.id ? String(dto.id) : undefined,
  categoryId: (dto.categoryId ?? null) as string | null,
  mediaType: String(dto.mediaType ?? 'image'),
  filePath: String(dto.filePath ?? ''),
  fileName: (dto.fileName ?? null) as string | null,
  isCover: Boolean(dto.isCover),
  isFeatured: Boolean(dto.isFeatured),
  displayOrder: (dto.displayOrder ?? null) as number | null,
})

export const parsePropertyDocumentItem = (dto: PropertyDocumentItemDto): PropertyDocumentItem => ({
  id: dto.id ? String(dto.id) : undefined,
  documentType: String(dto.documentType ?? ''),
  documentCategory: dto.documentCategory ?? null,
  documentTitle: dto.documentTitle ?? null,
  filePath: String(dto.filePath ?? ''),
  fileName: dto.fileName ?? null,
  fileSizeBytes: dto.fileSizeBytes ?? null,
  createdAt: dto.createdAt ?? null,
  documentVisibility: String(dto.documentVisibility ?? ''),
  titleDeedNumber: dto.titleDeedNumber ?? null,
  version: dto.version ?? null,
  // Absent on older payloads — treat as current rather than hiding the row.
  isLatest: dto.isLatest ?? true,
})

export const parsePropertyAmenityItem = (dto: PropertyAmenityItemDto): PropertyAmenityItem => ({
  id: dto.id ? String(dto.id) : undefined,
  amenityId: (dto.amenityId ?? null) as string | null,
  isCustom: Boolean(dto.isCustom),
  customName: (dto.customName ?? null) as string | null,
  customIcon: (dto.customIcon ?? null) as string | null,
  amenityNotes: (dto.amenityNotes ?? null) as string | null,
})

export const parsePropertyHighlightItem = (
  dto: PropertyHighlightItemDto,
): PropertyHighlightItem => ({
  id: dto.id ? String(dto.id) : undefined,
  highlightText: String(dto.highlightText ?? ''),
  highlightBrief: (dto.highlightBrief ?? null) as string | null,
  highlightIcon: (dto.highlightIcon ?? null) as string | null,
  displayOrder: (dto.displayOrder ?? null) as number | null,
})

export const parsePropertyValuation = (dto: PropertyValuationDto): PropertyValuation => ({
  id: dto.id ? String(dto.id) : undefined,
  valuationAmount: toNumberOrNull(dto.valuationAmount),
  valuationSource: (dto.valuationSource ?? null) as string | null,
  valuerName: (dto.valuerName ?? null) as string | null,
  valuationDate: (dto.valuationDate ?? null) as string | null,
  validFrom: (dto.validFrom ?? null) as string | null,
  validUntil: (dto.validUntil ?? null) as string | null,
  documentId: (dto.documentId ?? null) as string | null,
  valuationNotes: (dto.valuationNotes ?? null) as string | null,
  isLatest: Boolean(dto.isLatest),
})

export const parsePropertyOffering = (dto: PropertyOfferingDto): PropertyOffering => ({
  id: dto.id ? String(dto.id) : undefined,
  offeringType: String(dto.offeringType ?? 'primary'),
  offeringTitle: String(dto.offeringTitle ?? ''),
  offeringDescription: (dto.offeringDescription ?? null) as string | null,
  listingPrice: (dto.listingPrice ?? null) as number | null,
  pricePerToken: (dto.pricePerToken ?? null) as number | null,
  totalTokens: (dto.totalTokens ?? null) as number | null,
  minTokens: (dto.minTokens ?? null) as number | null,
  tokensSold: (dto.tokensSold ?? null) as number | null,
  totalEquityRaised: (dto.totalEquityRaised ?? null) as number | null,
  minInvestmentAmount: (dto.minInvestmentAmount ?? null) as number | null,
  maxInvestmentAmount: (dto.maxInvestmentAmount ?? null) as number | null,
  minFundingTarget: (dto.minFundingTarget ?? null) as number | null,
  investmentWindowStart: (dto.investmentWindowStart ?? null) as string | null,
  investmentWindowEnd: (dto.investmentWindowEnd ?? null) as string | null,
  rentalStartDate: (dto.rentalStartDate ?? null) as string | null,
  expectedRentalYield: (dto.expectedRentalYield ?? null) as number | null,
  rentalDistributionFrequency: String(dto.rentalDistributionFrequency ?? 'monthly'),
  expectedCapitalAppreciation: (dto.expectedCapitalAppreciation ?? null) as number | null,
  targetRoi5yr: (dto.targetRoi5yr ?? null) as number | null,
})

export const parseAuditLog = (dto: AuditLogDto): AuditLog => ({
  id: String(dto.id ?? ''),
  action: String(dto.action ?? ''),
  fromStatus: dto.fromStatus ? String(dto.fromStatus) : undefined,
  performedByName: dto.performedByName ? String(dto.performedByName) : undefined,
  description: dto.description ? String(dto.description) : undefined,
  createdAt: dto.createdAt ? String(dto.createdAt) : undefined,
  reason: dto.reason ? String(dto.reason) : undefined,
  attachment: dto.attachment,
})

// ─── Unified activity feed ──────────────────────────────────────────────────

const parsePropertyActivityRow = (dto: PropertyActivityRowDto): PropertyActivityEvent => ({
  id: String(dto.id ?? ''),
  type: dto.type,
  actor: dto.actorName ? String(dto.actorName) : 'System',
  createdAt: dto.createdAt ? String(dto.createdAt) : null,
  fromStatus: dto.fromStatus ?? null,
  toStatus: dto.toStatus ?? null,
  reason: dto.reason ?? null,
  rentalStatus: dto.rentalStatus ?? null,
  tenantType: dto.tenantType ?? null,
  annualRentAmount: dto.annualRentAmount != null ? Number(dto.annualRentAmount) : null,
  leaseStart: dto.leaseStart ?? null,
  leaseEnd: dto.leaseEnd ?? null,
  valuationAmount: dto.valuationAmount != null ? Number(dto.valuationAmount) : null,
  valuationSource: dto.valuationSource ?? null,
  validUntil: dto.validUntil ?? null,
})

export function parsePropertyActivityResponse(
  data: unknown,
): PaginatedResponse<PropertyActivityEvent> {
  const inner = (data as Record<string, unknown>)?.data ?? data
  const payload = inner as PropertyActivityResponseDto
  const meta = payload?.meta
  const limit = meta?.limit ?? 20
  const totalItems = meta?.total ?? 0
  return {
    data: (payload?.data ?? []).map(parsePropertyActivityRow),
    pagination: {
      page: meta?.page ?? 1,
      limit,
      totalItems,
      totalPages: meta?.totalPages ?? Math.max(1, Math.ceil(totalItems / limit)),
    },
  }
}

export const parseRefundItem = (dto: RefundItemDto): RefundItem => ({
  id: String(dto.id ?? ''),
  investorName: dto.investorName ? String(dto.investorName) : undefined,
  investedAmount: dto.investedAmount != null ? Number(dto.investedAmount) : undefined,
  orderStatus: dto.orderStatus ? String(dto.orderStatus) : undefined,
  refundStatus: dto.refundStatus ? String(dto.refundStatus) : undefined,
})

// ─── Settings ───────────────────────────────────────────────────────────────

export const parsePropertySetting = (dto: PropertySettingDto): PropertySetting => ({
  id: String(dto.id ?? ''),
  propertyId: String(dto.propertyId ?? ''),
  minInvestmentAmount: Number(dto.minInvestmentAmount ?? 0),
  maxOwnershipPctPerProperty: Number(dto.maxOwnershipPctPerProperty ?? 0),
  defaultFundingDeadlineDays: Number(dto.defaultFundingDeadlineDays ?? 0),
  primaryLockPeriodDays: Number(dto.primaryLockPeriodDays ?? 0),
  secondaryLockPeriodDays: Number(dto.secondaryLockPeriodDays ?? 0),
  kycExpiryDays: Number(dto.kycExpiryDays ?? 0),
  effectiveFrom: String(dto.effectiveFrom ?? ''),
  createdAt: dto.createdAt ? String(dto.createdAt) : undefined,
  createdBy: dto.createdBy ? String(dto.createdBy) : undefined,
})

export const parsePropertySettingsResponse = (data: unknown): PropertySetting[] => {
  const rawData = _get(data, 'data', data)
  const nested = _get(rawData, 'rows')
  const rows: unknown[] = Array.isArray(rawData) ? rawData : Array.isArray(nested) ? nested : []
  return (rows as PropertySettingDto[]).map(parsePropertySetting)
}

// ─── VASP onboarding lifecycle (Ctrl-Alt v2 three-tier) ──────────────────────

// PATCH :id/vasp/onboard | :id/vasp/retry | :id/vasp/sync response — the umbrella
// currentStatus, the three per-tier statuses + ids, failureStatus, retry count
// and the provider-freshness stamp. One parser for all three because
// admin-service returns one DTO for all three.
export const parseVaspOnboarding = (data: unknown): PropertyVaspOnboarding => {
  const inner = (_get(data, 'data', data) ?? {}) as Record<string, unknown>
  return {
    propertyStatus: String(_get(inner, 'propertyStatus') ?? ''),
    currentStatus: strOrNull(inner.currentStatus),
    vehicleStatus: strOrNull(inner.vehicleStatus),
    instrumentStatus: strOrNull(inner.instrumentStatus),
    assetStatus: strOrNull(inner.assetStatus),
    failureStatus: strOrNull(inner.failureStatus),
    retryCount: Number(_get(inner, 'retryCount') ?? 0) || 0,
    vehicleId: strOrNull(inner.vehicleId),
    instrumentId: strOrNull(inner.instrumentId),
    assetId: strOrNull(inner.assetId),
    lastSyncedAt: strOrNull(inner.lastSyncedAt),
    lastError: strOrNull(inner.lastError),
  }
}

// VASP block on the property read model. admin-service nests it under
// `vaspOnboarding` on both the detail and list routes; the `vasp` alias is read
// too so either key works. Absent → undefined, and the queue falls back to
// lifecycle-derived display.
export const parsePropertyVaspBlock = (
  raw: Record<string, unknown>,
): PropertyVaspBlock | undefined => {
  const node = raw.vaspOnboarding ?? raw.vasp
  if (!node || typeof node !== 'object') return undefined
  const src = node as Record<string, unknown>
  return {
    provider: strOrNull(src.provider ?? src.vaspProvider),
    currentStatus: strOrNull(src.currentStatus),
    vehicleStatus: strOrNull(src.vehicleStatus),
    instrumentStatus: strOrNull(src.instrumentStatus),
    assetStatus: strOrNull(src.assetStatus),
    vehicleId: strOrNull(src.vehicleId),
    instrumentId: strOrNull(src.instrumentId),
    assetId: strOrNull(src.assetId),
    failureStatus: strOrNull(src.failureStatus),
    tokenName: strOrNull(src.tokenName),
    blockchainNetwork: strOrNull(src.blockchainNetwork),
    retryCount: src.retryCount != null ? Number(src.retryCount) : null,
    lastError: strOrNull(src.lastError),
    lastErrorSource: strOrNull(src.lastErrorSource),
    lastRequestId: strOrNull(src.lastRequestId),
    lastSyncedAt: strOrNull(src.lastSyncedAt),
    vehicleSubmittedAt: strOrNull(src.vehicleSubmittedAt),
    instrumentSubmittedAt: strOrNull(src.instrumentSubmittedAt),
    dldValuation: src.dldValuation != null ? Number(src.dldValuation) : null,
    dldValuationDate: strOrNull(src.dldValuationDate),
  }
}

// Pre-flight VASP readiness block. Emitted by the property DETAIL read only —
// list rows send null, so this returns undefined there and the cockpit treats it
// as "not evaluated" rather than "ready".
export const parsePropertyVaspReadiness = (
  raw: Record<string, unknown>,
): PropertyVaspReadiness | undefined => {
  const node = raw.vaspReadiness
  if (!node || typeof node !== 'object') return undefined
  const src = node as PropertyVaspReadinessDto
  const missing = Array.isArray(src.missing) ? src.missing.map(String) : []
  return { ready: src.ready === true, missing }
}

// ─── Generic tab array extractor ────────────────────────────────────────────

export const extractTabArray = (res: unknown): unknown[] => {
  if (Array.isArray(res)) return res
  if (res && typeof res === 'object') {
    const r = res as Record<string, unknown>
    if (r.rows && Array.isArray(r.rows)) return r.rows
    if (r.items && Array.isArray(r.items)) return r.items
    if (r.data) {
      if (Array.isArray(r.data)) return r.data
      if (typeof r.data === 'object' && r.data !== null) {
        const d = r.data as Record<string, unknown>
        if (d.rows && Array.isArray(d.rows)) return d.rows
        if (d.items && Array.isArray(d.items)) return d.items
        // Double-wrapped envelope ({ data: { data: [...] } }) — some endpoints
        // return their own { success, data } which the response interceptor
        // wraps again, burying the array one level deeper.
        if (Array.isArray(d.data)) return d.data
      }
    }
  }
  return []
}

// ─── Params lowering ────────────────────────────────────────────────────────

export const toPropertiesApiParamsDto = (input: PropertiesApiParams): PropertiesApiParamsDto => ({
  page: input.page,
  limit: input.limit,
  search: input.search,
  propertyType: input.propertyType,
  propertyStatus: input.propertyStatus,
  marketId: input.marketId,
  countryId: input.countryId,
})

// ─── Refresh queues (property-refresh-queues-plan.md) ────────────────────────

// Imports for parser — keep them near usage to avoid polluting the top-of-file
// import block while the parser is still being expanded.
import type {
  RefreshQueuePropertyHeaderDto,
  RentalRefreshCountsDto,
  RentalRefreshQueueLatestDto,
  RentalRefreshQueueResponseDto,
  RentalRefreshQueueRowDto,
  ValuationRefreshCountsDto,
  ValuationRefreshQueueLatestDto,
  ValuationRefreshQueueResponseDto,
  ValuationRefreshQueueRowDto,
} from './propertyContracts'
import type {
  RefreshQueuePropertyHeader,
  RentalRefreshCounts,
  RentalRefreshLatest,
  RentalRefreshQueueResponse,
  RentalRefreshQueueRow,
  ValuationRefreshCounts,
  ValuationRefreshLatest,
  ValuationRefreshQueueResponse,
  ValuationRefreshQueueRow,
} from '../types/propertyTypes'

function parseRefreshQueuePropertyHeader(
  dto: RefreshQueuePropertyHeaderDto,
): RefreshQueuePropertyHeader {
  return {
    id: dto.id,
    propertyName: dto.propertyName,
    propertyRefCode: dto.propertyRefCode,
    propertyType: dto.propertyType,
    city: dto.city,
    areaName: dto.areaName,
    currencyCode: dto.currencyCode,
  }
}

function parseRentalRefreshLatest(dto: RentalRefreshQueueLatestDto): RentalRefreshLatest {
  return {
    id: dto.id,
    status: dto.status,
    tenantType: dto.tenantType,
    tenantName: dto.tenantName,
    monthlyRentAmount: dto.monthlyRentAmount,
    annualRentAmount: dto.annualRentAmount,
    leaseStart: dto.leaseStart,
    leaseEnd: dto.leaseEnd,
    source: dto.source,
    createdAt: dto.createdAt,
  }
}

export function parseRentalRefreshQueueRow(dto: RentalRefreshQueueRowDto): RentalRefreshQueueRow {
  return {
    property: parseRefreshQueuePropertyHeader(dto.property),
    latestRentalPeriod: dto.latestRentalPeriod
      ? parseRentalRefreshLatest(dto.latestRentalPeriod)
      : null,
    bucket: dto.bucket,
  }
}

export function parseRentalRefreshQueueResponse(data: unknown): RentalRefreshQueueResponse {
  const inner = (data as Record<string, unknown>)?.data ?? data
  const payload = inner as RentalRefreshQueueResponseDto
  return {
    rows: (payload.rows ?? []).map(parseRentalRefreshQueueRow),
    total: payload.meta?.total ?? 0,
    page: payload.meta?.page ?? 1,
    limit: payload.meta?.limit ?? 20,
    totalPages: payload.meta?.totalPages ?? 0,
  }
}

export function parseRentalRefreshCounts(data: unknown): RentalRefreshCounts {
  const inner = (data as Record<string, unknown>)?.data ?? data
  const dto = inner as RentalRefreshCountsDto
  return {
    total: dto.total ?? 0,
    expired: dto.expired ?? 0,
    expiringSoon: dto.expiringSoon ?? 0,
    vacancyStale: dto.vacancyStale ?? 0,
    valid: dto.valid ?? 0,
  }
}

function parseValuationRefreshLatest(dto: ValuationRefreshQueueLatestDto): ValuationRefreshLatest {
  return {
    id: dto.id,
    valuationAmount: Number(dto.valuationAmount),
    valuationSource: dto.valuationSource,
    valuerName: dto.valuerName,
    valuationDate: dto.valuationDate,
    validFrom: dto.validFrom,
    validUntil: dto.validUntil,
    documentId: dto.documentId,
    createdAt: dto.createdAt,
  }
}

export function parseValuationRefreshQueueRow(
  dto: ValuationRefreshQueueRowDto,
): ValuationRefreshQueueRow {
  return {
    property: parseRefreshQueuePropertyHeader(dto.property),
    latestValuation: dto.latestValuation ? parseValuationRefreshLatest(dto.latestValuation) : null,
    bucket: dto.bucket,
    hasActiveOffering: dto.hasActiveOffering,
  }
}

export function parseValuationRefreshQueueResponse(data: unknown): ValuationRefreshQueueResponse {
  const inner = (data as Record<string, unknown>)?.data ?? data
  const payload = inner as ValuationRefreshQueueResponseDto
  return {
    rows: (payload.rows ?? []).map(parseValuationRefreshQueueRow),
    total: payload.meta?.total ?? 0,
    page: payload.meta?.page ?? 1,
    limit: payload.meta?.limit ?? 20,
    totalPages: payload.meta?.totalPages ?? 0,
  }
}

export function parseValuationRefreshCounts(data: unknown): ValuationRefreshCounts {
  const inner = (data as Record<string, unknown>)?.data ?? data
  const dto = inner as ValuationRefreshCountsDto
  return {
    total: dto.total ?? 0,
    missing: dto.missing ?? 0,
    expired: dto.expired ?? 0,
    expiringSoon: dto.expiringSoon ?? 0,
    valid: dto.valid ?? 0,
  }
}
